import {
  createContext, useCallback, useContext, useMemo,
} from 'react'
import PropTypes from 'prop-types'
import * as amplitude from '@amplitude/analytics-browser'

const CoachProfileAnalyticsContext = createContext(null)

export function buildCoachProfileAnalyticsProperties(coach) {
  const coachName = coach?.displayName
    || [coach?.firstName, coach?.lastName].filter(Boolean).join(' ')
  return {
    coach_id: coach?.coach?.id || null,
    coach_profile_id: coach?.coachProfileId || null,
    coach_name: coachName || null,
    bookable: Boolean(coach?.bookable),
    has_plus_subscription: Boolean(coach?.coach?.hasPaidTierSubscription),
    member_type: coach?.coach?.memberType || null,
    certification_level: coach?.certificationLevel || null,
  }
}

/**
 * Scopes coach-profile Amplitude events to the flagged profile page.
 * Components shared with other surfaces call `useTrackCoachProfileEvent`
 * and emit nothing when rendered outside this provider.
 */
export function CoachProfileAnalyticsProvider({ coach, children }) {
  const value = useMemo(
    () => buildCoachProfileAnalyticsProperties(coach),
    [
      coach?.coach?.id,
      coach?.coachProfileId,
      coach?.displayName,
      coach?.firstName,
      coach?.lastName,
      coach?.bookable,
      coach?.coach?.hasPaidTierSubscription,
      coach?.coach?.memberType,
      coach?.certificationLevel,
    ],
  )
  return (
    <CoachProfileAnalyticsContext.Provider value={value}>
      {children}
    </CoachProfileAnalyticsContext.Provider>
  )
}

CoachProfileAnalyticsProvider.propTypes = {
  coach: PropTypes.object,
  children: PropTypes.node,
}

export function useTrackCoachProfileEvent() {
  const base = useContext(CoachProfileAnalyticsContext)
  return useCallback((event, properties = {}) => {
    if (!base) return
    amplitude.track(event, {
      ...base,
      page_path: typeof window !== 'undefined' ? window.location.pathname : null,
      ...properties,
    })
  }, [base])
}
